import {
	EnvironmentContextProvider,
	LayoutProvider,
	MainNavigation,
	config,
	createCollateralLibraryRoutes,
	createLegalRoutes,
	createNewsRoutes,
	createResourceRoutes,
	useLayoutContext,
	ThemeProvider,
	type LayoutNavComponent,
} from '@ds-mgnl/marketing';
import type { LinkSourceProps, LinkSourceRecord } from '@dsx/react';
import { Provider as DsxProvider } from '@dsx/react';
import { forwardRef, useCallback } from 'react';
import {
	Outlet,
	Link as RouterLink,
	RouterProvider,
	createBrowserRouter,
} from 'react-router-dom';
import styles from './App.module.css';
import { DsLogo } from './components/DsLogo';
import { Footer } from './components/Footer';
import { topNavData } from './components/TopNav/topNavData';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useUtmFormTracking } from './hooks/useUtmFormTracking';
import { ErrorPage } from './pages/ErrorPage';
import { MagnoliaPage } from './pages/MagnoliaPage';
import { magnoliaPageLoader } from './pages/magnoliaPageLoader';
import { getMagnoliaBaseDamUrl, getMagnoliaBaseUrl } from './util';
import { ctaLinks } from './components';

const linkSources = {
	default: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<RouterLink ref={ref} {...props} to={props.href || ''} />
	)),
	external: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<a ref={ref} {...props} />
	)),
	none: forwardRef<HTMLSpanElement, LinkSourceProps>((props, ref) => (
		<span ref={ref} {...props} />
	)),
} as LinkSourceRecord;

const router = createBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			...createResourceRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				resourcesHomeUrlPath: '/resources',
				pageConfig: config,
				showOnSite: 'directsupply',
			}),
			...createLegalRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				legalHomeUrlPath: '/legal',
				pageConfig: config,
				showOnSite: 'directsupply',
			}),
			...createCollateralLibraryRoutes({
				errorElement: <ErrorPage />,
				collateralLibUrlPath: '/collateral-library',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				mgnlBaseDamUrl: getMagnoliaBaseDamUrl() ?? '',
				mgnlCollateralHomePath: '/collateral-library',
				siteTitleSuffix: 'Direct Supply',
				showOnSite: 'directsupply',
			}),
			...createNewsRoutes({
				newsHomeUrlPath: '/news',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				pageConfig: config,
				errorElement: <ErrorPage />,
				showOnSite: 'directsupply',
			}),
			{
				path: '*',
				element: <MagnoliaPage />,
				loader: magnoliaPageLoader,
				errorElement: <ErrorPage />,
			},
		],
	},
]);

export function App() {
	useUtmFormTracking();
	const navigationComponent = useCallback<LayoutNavComponent>(
		({ transparent }) => (
			<MainNavigation
				transparent={transparent}
				navData={topNavData}
				navLogo={DsLogo}
				ctaLinks={ctaLinks}
			/>
		),
		[],
	);

	return (
		<DsxProvider locale="en-US" linkSources={linkSources}>
			<ThemeProvider theme="oneDS">
				<EnvironmentContextProvider
					value={{
						damHost: getMagnoliaBaseDamUrl() ?? '',
						appHost: getMagnoliaBaseUrl() ?? '',
						isInMagnolia: false,
					}}
				>
					<LayoutProvider initialNavigationComponent={navigationComponent}>
						<RouterProvider router={router} />
					</LayoutProvider>
				</EnvironmentContextProvider>
			</ThemeProvider>
		</DsxProvider>
	);
}

function RootLayout() {
	useScrollManagement();
	const { renderNavigation } = useLayoutContext();

	return (
		<div className={styles['site-container']}>
			{renderNavigation({
				transparencyOverlapRatio: 1,
				transparencyOverlapType: 'vertical',
			})}
			<main>
				<Outlet />
			</main>
			<Footer />
		</div>
	);
}
